import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Tag, Menu, Dropdown, Modal, message, PageHeader, Typography, Space } from 'antd';
import { apiClient, apiErrorMessage } from '../../../utils';
import { Field, Grid, GridRow, Integration, IntegrationExecution } from '../../../models';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { Content } from 'antd/lib/layout/layout';
import moment from 'moment';
import { cronParser } from '../../../utils/cron-parser';
import { EditIntegrationExportFilterPage } from './edit-integration-export-filter-page';
import { DataPreviewPage } from './data-preview-page';
import { FilterViewer } from '../../../elements';

const operatorMap = [
  { value: '==', name: 'eşit' },
  { value: '!=', name: 'eşit değil' },
  { value: '>', name: 'büyük' },
  { value: '>=', name: 'büyük eşit' },
  { value: '<', name: 'küçük' },
  { value: '<=', name: 'küçük eşit' },
  { value: 'in', name: 'içinde' },
  { value: '!in', name: 'içinde değil' },
  { value: 'startsWith', name: 'başlar' },
  { value: 'endsWith', name: 'biter' },
  { value: 'contains', name: 'içerir' },
  { value: 'null', name: 'boş' },
  { value: '!null', name: 'boş değil' },
];

export function IntegrationExportFilterPage(props: { integration?: Integration; onSaved: (integration: Integration) => void }) {
  const [grid, setGrid] = useState<Grid>();
  const [editIntegrationVisible, setEditIntegrationVisible] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<any>();
  const [newFilterVisible, setNewFilterVisible] = useState(false);
  const [filters, setFilters] = useState<any[]>();
  const [showPreview, setShowPreview] = useState(false);
  const [showPreviewFilterName, setShowPreviewFilterName] = useState<string>();
  const [fkRecords, setFkRecords] = useState<GridRow[]>();

  const { Text } = Typography;

  useEffect(() => {
    const gridId = props.integration?.primaryGridId;
    const filters = props.integration?.filter;
    setFilters(filters);
    apiClient
      .get(`grids/${gridId}`)
      .then((response: any) => {
        const responseGrid = response.data as Grid;
        setGrid(responseGrid);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  }, [props.integration]);

  const loadFkRecords = useCallback(async () => {
    if (!filters?.length || !grid) {
      return;
    }

    const newFkRecords: GridRow[] = [];

    for (const filter of filters) {
      if (!filter.filterRules?.length) {
        continue;
      }

      for (const rule of filter.filterRules) {
        const field = grid?.fields.find((f: Field) => f.id === rule.fieldId);
        if (field?.dataType === 'FOREIGNKEY' || field?.dataType === 'MULTIFOREIGNKEY') {
          const fkGridId = field.foreignKeyGridId;
          const fkRecordIds = Array.isArray(rule.value) ? rule.value : [rule.value];

          for (const fkRecordId of fkRecordIds) {
            await apiClient
              .get(`records/record?gridId=${fkGridId}&rowId=${fkRecordId}`)
              .then((response: any) => {
                newFkRecords.push(response.data);
              })
              .catch(() => {});
          }
        }
      }
    }

    setFkRecords(newFkRecords?.length ? newFkRecords : undefined);
  }, [filters, grid]);

  useEffect(() => {
    loadFkRecords();
  }, [loadFkRecords]);

  const saveFilter = (newFilters: any) => {
    apiClient
      .put(`integrations/${props.integration?.id}/filter`, { filter: newFilters })
      .then((response: any) => {
        message.success('Dışa aktarım filtresi başarıyla kaydedildi.');
        props.onSaved(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  };

  const onFieldGroupActionClick = (e: any, index: number) => {
    if (e.key === 'edit') {
      setSelectedFilter(index);
      setEditIntegrationVisible(true);
    } else if (e.key === 'preview') {
      const filterName = encodeURIComponent(filters?.[index].filterName);
      setShowPreview(true);
      setShowPreviewFilterName(filterName);
    } else if (e.key === 'run') {
      Modal.confirm({
        title: 'Filtreyi çalıştır',
        content: 'Entegrasyonu bu filtre ile çalıştırmak istediğinize emin misiniz?',
        onOk: () => {
          const filterName = encodeURIComponent(filters?.[index].filterName);
          const integrationId = props.integration?.id;
          apiClient
            .get(`integrations/${integrationId}/execute?filtername=${filterName}`)
            .then((response: any) => {
              const execution = response.data as IntegrationExecution;
              if (execution.hasError) {
                message.error(execution.errorMessage);
              } else {
                if (execution.recordCount) {
                  message.success(execution.recordCount + ' adet kayıt başarıyla aktarıldı');
                } else {
                  message.info('Herhangi bir kayıt aktarımı yapılmadı.');
                }
              }
            })
            .catch((reason: any) => {
              console.log('exportSelectedRows - API Error', { error: reason });
              apiErrorMessage(reason);
            });
        },
      });
    } else if (e.key === 'del') {
      Modal.confirm({
        title: 'Filtreyi sil',
        content: 'Filtreyi silmek istediğinize emin misiniz?',
        onOk: () => {
          const newFilters = [...(filters as any[])];
          newFilters.splice(index, 1);
          saveFilter(newFilters);
        },
      });
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    setShowPreviewFilterName(undefined);
  };

  const openNewFilterModal = () => {
    setNewFilterVisible(true);
  };

  return (
    <>
      <PageHeader
        extra={[
          <Button key="add" onClick={openNewFilterModal} icon={<PlusOutlined />} type="primary">
            Yeni filtre
          </Button>,
        ]}
      />
      <Content>
        <Modal
          width={'80%'}
          title="Filtre düzenle"
          visible={editIntegrationVisible}
          onCancel={() => setEditIntegrationVisible(false)}
          footer={null}
          destroyOnClose
        >
          <EditIntegrationExportFilterPage
            filter={filters?.[selectedFilter]}
            filters={filters}
            onSaved={(formData: any) => {
              const newFilters = [...(filters as any[])];
              newFilters[selectedFilter] = {
                ...newFilters[selectedFilter],
                ...formData,
              };
              saveFilter(newFilters);
            }}
            grid={grid}
          />
        </Modal>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '1rem',
          }}
        >
          {filters?.map((filter, index) => {
            return (
              <Card
                key={index}
                title={filter.filterName}
                headStyle={{
                  background: '#EFF2F5',
                }}
                style={{ width: '25rem', height: '25rem' }}
                bodyStyle={{ padding: '1rem' }}
                extra={
                  <Dropdown
                    overlay={
                      <Menu onClick={(e: any) => onFieldGroupActionClick(e, index)}>
                        <Menu.Item key="preview">Veri önizleme</Menu.Item>
                        <Menu.Item key="run">Yeni aktarım başlat</Menu.Item>
                        <Menu.Divider />
                        <Menu.Item key="edit">Düzenle</Menu.Item>
                        <Menu.Item key="del">
                          <Text type="danger">Sil</Text>
                        </Menu.Item>
                      </Menu>
                    }
                    trigger={['click']}
                  >
                    <Button size="small" icon={<EllipsisOutlined />} />
                  </Dropdown>
                }
              >
                <Space direction="vertical" size={20}>
                  <Space direction="vertical">
                    <Text strong>Koşullar</Text>
                    {filter?.filterRules?.length ? (
                      filter?.filterRules?.map((item: any, i: number) => {
                        const { fieldId, op, value } = item;
                        const field = grid?.fields.find((f: Field) => f.id === fieldId);
                        let fieldLabel = field?.name;
                        let opLabel = operatorMap.find((o: any) => o.value === op)?.name;
                        if (fieldId === 'change_start_date') {
                          fieldLabel = 'Değişiklik başlangıç tarihi';
                          opLabel = 'eşit';
                        }
                        let valueDisplay = value;
                        if (field?.dataType === 'FOREIGNKEY') {
                          const fkRecord = fkRecords?.find(r => r.id === value);
                          if (fkRecord && field?.foreignKeyFieldId) {
                            if (Array.isArray(value)) {
                              valueDisplay = value.map(v => fkRecord.cells[field?.foreignKeyFieldId].value);
                            } else {
                              valueDisplay = fkRecord.cells[field?.foreignKeyFieldId].value;
                            }
                          }
                        }
                        if (field?.dataType === 'MULTIFOREIGNKEY') {
                          valueDisplay =
                            (value as string[])?.map(v => {
                              const fkRecord = fkRecords?.find(r => r.id === v);
                              if (fkRecord && field?.foreignKeyFieldId) {
                                return fkRecord.cells[field?.foreignKeyFieldId].value;
                              }
                              return v;
                            }) || value;
                        } else if (field?.dataType === 'PICKLIST') {
                          if (field?.picklistValues?.length) {
                            if (Array.isArray(value)) {
                              valueDisplay = value.map(v => field.picklistValues.find(p => p.id === v)?.val || v);
                            } else {
                              valueDisplay = field.picklistValues.find(p => p.id === value)?.val || value;
                            }
                          }
                        } else if (field?.dataType === 'MULTIPICKLIST') {
                          if (field?.picklistValues?.length) {
                            valueDisplay =
                              (value as string[])?.map(v => {
                                const picklistValue = field.picklistValues.find(p => p.id === v);
                                return picklistValue ? picklistValue.val : v;
                              }) || value;
                          }
                        } else if (field?.dataType === 'BOOLEAN') {
                          valueDisplay = value === true ? 'Evet' : 'Hayır';
                        } else if (fieldId === 'change_start_date') {
                          valueDisplay = moment(value).format('DD.MM.YYYY');
                        }

                        if (Array.isArray(valueDisplay)) {
                          valueDisplay = valueDisplay?.join(', ');
                        }

                        return (
                          <p key={i} style={{ margin: 0 }}>
                            {fieldLabel} <Tag>{opLabel}</Tag>
                            {valueDisplay}
                          </p>
                        );
                      })
                    ) : (
                      <span>Tümü</span>
                    )}
                  </Space>
                  <Space direction="vertical">
                    <Text strong>Filtre</Text>
                    <FilterViewer dataSetId={props.integration?.primaryGridId} filterGroup={filter.filterGroup} size="small" />
                  </Space>
                  <Space direction="vertical">
                    <Text strong>Otomatik çalıştırma</Text>
                    {filter.cronStrings?.length ? (
                      filter.cronStrings?.map((cronString: any, i: number) => {
                        const parsed = cronParser(cronString.value);
                        return (
                          <p key={i} style={{ margin: 0 }}>
                            {parsed.description}, {cronString.sendType === 'all' ? 'tüm' : 'değişen'} kayıtlar
                          </p>
                        );
                      })
                    ) : (
                      <span>Yok</span>
                    )}
                  </Space>
                </Space>
              </Card>
            );
          })}
        </div>
      </Content>
      <Modal width={'80%'} visible={newFilterVisible} onCancel={() => setNewFilterVisible(false)} footer={null} title="Yeni filtre" destroyOnClose>
        <EditIntegrationExportFilterPage
          filter={undefined}
          filters={filters}
          onSaved={(formData: any) => {
            const newFilters = [...(filters as any[]), formData];
            saveFilter(newFilters);
          }}
          grid={grid}
        />
      </Modal>
      <Modal title="Veri önizleme" visible={showPreview} footer={null} onCancel={closePreview} destroyOnClose={true} width={'90%'}>
        <DataPreviewPage integrationId={props.integration?.id} filterName={showPreviewFilterName} />
      </Modal>
    </>
  );
}
